import { Button, TextField, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import React, { forwardRef, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';
import { nanoid } from 'nanoid';
import palette from '../../theme/palette';
import FileUploadContainer from '../FileUploadContainer';
import OMRDialog from './OMRDialog';
import DownloadFileContainer from '../DownloadFileContainer';
import { OPTIONS_LABEL } from '../../utils/constants';

const useStyles = makeStyles({
  titleText: {
    marginTop: 25,
    fontSize: 16
  },
  subText: {
    fontSize: 14,
    color: palette.grey[500]
  }
});

const UploadPaper = forwardRef(({ testObj, handleNext }, ref) => {
  const classes = useStyles();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [totalQuestions, setTotalQuestions] = useState(testObj?.numOfQuestions ?? '');
  const [totalOptions, setTotalOptions] = useState(testObj?.numOfOptions ?? '');
  const [selectedFile, setSelectedFile] = useState(testObj?.testFile ?? null);
  const [fileError, setFileError] = useState(false);
  const [quesErr, setQuesErr] = useState(false);
  const [optsErr, setOptsErr] = useState(false);
  const [questionsArray, setQuesArray] = useState(testObj?.questions ?? []);

  const checkValidData = () => {
    const isFileErr = selectedFile !== null;
    let isTotalQueErr = true;
    let isTotalOptErr = true;
    setFileError(selectedFile === null);

    if (totalQuestions !== '') {
      if (totalOptions === 0) {
        isTotalOptErr = false;
        setOptsErr(true);
      } else if (totalOptions === '') {
        isTotalOptErr = false;
        setOptsErr(true);
      } else if (totalOptions > 5) {
        isTotalOptErr = false;
        setOptsErr(true);
      } else {
        setOptsErr(false);
      }
    } else {
      setOptsErr(false);
    }

    if (totalOptions !== '') {
      if (totalQuestions === 0) {
        isTotalQueErr = false;
        setQuesErr(true);
      } else if (totalQuestions === '') {
        isTotalQueErr = false;
        setQuesErr(true);
      } else {
        setQuesErr(false);
      }
    } else {
      setQuesErr(false);
    }
    return isFileErr && isTotalQueErr && isTotalOptErr;
  };

  const makeArrayOfQuestion = (questionNum, noQuestionAns) => {
    // eslint-disable-next-line
    const numOfQues = parseInt(questionNum);
    // eslint-disable-next-line
    const numOfAns = parseInt(noQuestionAns);
    const questionArr = [];
    if (numOfQues !== 0 && numOfQues !== '' && numOfAns !== 0 && numOfAns !== '') {
      for (let i = 0; i < numOfQues; i += 1) {
        const ansArr = [];
        for (let j = 0; j < numOfAns; j += 1) {
          const obj = {
            _id: nanoid(),
            isAnswer: false,
            value: OPTIONS_LABEL[j].toLowerCase()
          };
          ansArr.push(obj);
        }
        questionArr.push({
          _id: nanoid(),
          questionNumber: `${i + 1}`,
          options: ansArr
        });
      }
      setQuesArray(questionArr);
    }
  };

  const checkOMRAnswer = () => {
    let isValid = true;
    for (let i = 0; i < questionsArray.length; i += 1) {
      const element = questionsArray[i];
      let objValid = true;
      for (let j = 0; j < element.options.length; j += 1) {
        const subElement = element.options[j];
        if (subElement.isAnswer) {
          objValid = true;
          break;
        } else {
          objValid = false;
        }
      }
      if (!objValid && isValid) {
        isValid = false;
      }
    }
    return isValid;
  };

  useImperativeHandle(ref, () => ({
    handleUploadPaperSubmit() {
      if (checkValidData()) {
        // If user added question number and answer per question
        if (totalQuestions !== '' && totalOptions !== '') {
          // Check for answer is given
          if (checkOMRAnswer()) {
            const obj = {
              testFile: selectedFile,
              questions: questionsArray,
              numOfQuestions: parseInt(totalQuestions, 10),
              numOfOptions: parseInt(totalOptions, 10)
            };
            handleNext(obj);
          } else {
            setDialogOpen(true);
          }
        } else {
          const obj = {
            testFile: selectedFile
          };
          handleNext(obj);
        }
      }
    }
  }));

  return (
    <div style={{ marginTop: 15 }}>
      <Typography className={classes.titleText}>Upload question paper</Typography>
      <Typography className={classes.subText}>
        Select one file at a time.You ca use PDF or image.
      </Typography>
      <div style={{ marginTop: 20 }}>
        {selectedFile ? (
          <DownloadFileContainer
            title={selectedFile.name}
            onCancelClick={() => setSelectedFile(null)}
          />
        ) : (
          <FileUploadContainer
            onFileChange={(e) => {
              setSelectedFile(e);
              setFileError(false);
            }}
          />
        )}
        {fileError && <Typography color="red">Please select paper</Typography>}
      </div>
      <TextField
        label="No. of questions"
        placeholder="Enter no. of questions"
        value={totalQuestions}
        onChange={(e) => {
          setTotalQuestions(e.target.value);
          makeArrayOfQuestion(e.target.value, totalOptions);
        }}
        fullWidth
        type="number"
        error={quesErr}
        helperText={quesErr ? 'Please enter questions' : ''}
        margin="dense"
        sx={{
          marginTop: 5
        }}
      />
      <TextField
        label="No. of options per question"
        placeholder="Enter no. of options"
        value={totalOptions}
        onChange={(e) => {
          setTotalOptions(e.target.value);
          setOptsErr(false);
          if (e.target.value > 5) {
            setOptsErr(true);
          } else {
            makeArrayOfQuestion(totalQuestions, e.target.value);
          }
        }}
        fullWidth
        type="number"
        error={optsErr}
        helperText={
          // eslint-disable-next-line
          optsErr ? (!totalOptions ? 'Please enter options' : 'Options not more then 5') : ''
        }
        margin="dense"
        sx={{
          marginTop: 2
        }}
      />
      {totalQuestions !== '' && !optsErr && totalOptions !== '' && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
          <Button onClick={() => setDialogOpen(true)}>Mark Answers</Button>
        </div>
      )}
      <OMRDialog
        isOpen={dialogOpen}
        questionsArray={questionsArray}
        numOfAns={totalOptions}
        onClose={() => setDialogOpen(false)}
        updateQuestionArray={(d) => setQuesArray(d)}
      />
    </div>
  );
});

UploadPaper.propTypes = {
  testObj: PropTypes.shape({
    numOfQuestions: PropTypes.string,
    numOfOptions: PropTypes.string,
    testFile: PropTypes.any,
    questions: PropTypes.array
  }),
  handleNext: PropTypes.func
};

export default UploadPaper;
